import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as uuid from 'uuid';

import { ConfigAllService } from 'src/app/global/services/config-all/config-all.abstract.service';
import { DenialReason, PatchOrganisationConfiguration } from 'src/app/global/services/config-all/config-all.model';
import { BcitiContextService } from 'src/app/global/services/context/context.abstract.service';
import { ConfirmDialogV2Component } from '../../dialogs/confirm-dialog-v2/confirm-dialog-v2.component';
import { DenialReasonDialogComponent, DenialReasonDialogData } from '../../dialogs/denial-reason-dialog/denial-reason-dialog.component';

@Component({
    selector: 'app-denial-reasons-table',
    templateUrl: './denial-reasons-table.component.html',
    styleUrls: ['./denial-reasons-table.component.scss'],
})
export class DenialReasonsTableComponent implements OnInit, OnChanges, OnDestroy {
    @Input() group: 'support' | 'family' | 'residence';
    @Input() denialReasons: { [lang: string]: DenialReason[] };
    @Input() langs: string[];
    @Input() orgTag: string;
    @Input() cardType: string;
    @Input() orgConfig: any;
    @Input() sectionLabel: string;

    destroy$ = new Subject();
    saving = false;
    selectedLang: string;
    displayedColumns = ['dragHandle', 'title', 'internalDescription', 'citizenReason', 'actions'];
    dataSource = new MatTableDataSource<DenialReason>();
    columnHeaders: { [lang: string]: { [key: string]: string } } = {};

    private reasonsByLang: { [lang: string]: DenialReason[] } = {};

    constructor(
        public readonly translate: TranslateService,
        private readonly dialog: MatDialog,
        private readonly configAllService: ConfigAllService,
        @Inject('BCITI_CONTEXT_SERVICE') private readonly contextService: BcitiContextService,
    ) {}

    ngOnInit(): void {
        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.loadColumnHeaders();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.denialReasons) {
            this.initData();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initData(): void {
        this.reasonsByLang = {};
        if (this.denialReasons && this.langs) {
            if (!this.selectedLang) {
                this.selectedLang = this.translate.currentLang || this.langs[0] || 'fr';
            }
            for (const lang of this.langs) {
                this.reasonsByLang[lang] = (this.denialReasons[lang] || []).map((r) => ({ ...r })).sort((a, b) => a.order - b.order);
            }
            this.loadColumnHeaders();
        }
        this.updateDataSource();
    }

    private loadColumnHeaders(): void {
        const tableKeys = ['title', 'internalDescription', 'citizenReason', 'actions'];
        const langsToLoad = new Set([...this.langs, this.translate.currentLang]);
        for (const lang of langsToLoad) {
            if (!lang) continue;
            const translations = this.translate.translations[lang];
            this.columnHeaders[lang] = {};
            for (const key of tableKeys) {
                this.columnHeaders[lang][key] = this.resolveNestedKey(translations, `pages.config-all.denial-reasons.table.${key}`) || '';
            }
        }
    }

    private resolveNestedKey(obj: any, key: string): string {
        return key.split('.').reduce((o, k) => (o?.[k] !== undefined ? o[k] : null), obj);
    }

    getHeader(key: string): string {
        const uiLang = this.translate.currentLang || this.langs?.[0] || 'fr';
        return this.columnHeaders[uiLang]?.[key] || '';
    }

    switchLang(lang: string): void {
        this.selectedLang = lang;
        this.updateDataSource();
    }

    private updateDataSource(): void {
        this.dataSource.data = [...(this.reasonsByLang[this.selectedLang] || [])];
    }

    openAddDialog(): void {
        const dialogData: DenialReasonDialogData = {
            mode: 'add',
            langs: this.langs,
        };

        const dialogRef = this.dialog.open(DenialReasonDialogComponent, {
            autoFocus: false,
            panelClass: 'bciti-dialog',
            data: dialogData,
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    this.addReason(result);
                }
            });
    }

    openEditDialog(reason: DenialReason): void {
        const reasonData: { [lang: string]: { title: string; internalDescription: string; citizenReason: string } } = {};
        for (const lang of this.langs) {
            const langReason = (this.reasonsByLang[lang] || []).find((r) => r.id === reason.id);
            if (langReason) {
                reasonData[lang] = {
                    title: langReason.title,
                    internalDescription: langReason.internalDescription,
                    citizenReason: langReason.citizenReason,
                };
            } else {
                reasonData[lang] = { title: '', internalDescription: '', citizenReason: '' };
            }
        }

        const dialogData: DenialReasonDialogData = {
            mode: 'edit',
            langs: this.langs,
            reason: reasonData,
        };

        const dialogRef = this.dialog.open(DenialReasonDialogComponent, {
            autoFocus: false,
            panelClass: 'bciti-dialog',
            data: dialogData,
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    this.editReason(reason.id, result);
                }
            });
    }

    openDeleteDialog(reason: DenialReason): void {
        const confirmDialogRef = this.dialog.open(ConfirmDialogV2Component, {
            autoFocus: false,
            panelClass: 'bciti-confirmation-dialog',
            data: {
                title: this.translate.instant('pages.config-all.denial-reasons.deleteDialog.title'),
                body: `<span class="denial-reason-warning-icon">⚠</span>${this.translate.instant('pages.config-all.denial-reasons.deleteDialog.body', { title: `<strong>${reason.title}</strong>` })}`,
                buttons: {
                    cancel: {
                        text: this.translate.instant('pages.config-all.denial-reasons.deleteDialog.cancel'),
                        class: 'denial-reason-cancel-btn',
                    },
                    confirm: {
                        text: this.translate.instant('pages.config-all.denial-reasons.deleteDialog.confirm'),
                        class: 'denial-reason-delete-btn',
                    },
                },
            },
        });

        confirmDialogRef.componentInstance.onSubmit.pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
            if (confirmed) {
                confirmDialogRef.componentInstance.loading = true;
                this.deleteReason(reason.id, confirmDialogRef);
            } else {
                confirmDialogRef.close();
            }
        });
    }

    drop(event: CdkDragDrop<DenialReason[]>): void {
        if (event.previousIndex === event.currentIndex) return;

        for (const lang of this.langs) {
            moveItemInArray(this.reasonsByLang[lang], event.previousIndex, event.currentIndex);
            this.reasonsByLang[lang].forEach((r, i) => (r.order = i + 1));
        }
        this.updateDataSource();
        this.save(this.translate.instant('pages.config-all.denial-reasons.messages.reorderSuccess'));
    }

    private addReason(formValue: { [lang: string]: { title: string; internalDescription: string; citizenReason: string } }): void {
        const id = uuid.v4();
        const maxOrder = this.getMaxOrder();

        for (const lang of this.langs) {
            if (!this.reasonsByLang[lang]) {
                this.reasonsByLang[lang] = [];
            }
            this.reasonsByLang[lang].push({
                id,
                title: formValue[lang].title,
                internalDescription: formValue[lang].internalDescription,
                citizenReason: formValue[lang].citizenReason,
                order: maxOrder + 1,
            });
        }
        this.updateDataSource();
        this.save(this.translate.instant('pages.config-all.denial-reasons.messages.addSuccess'));
    }

    private editReason(id: string, formValue: { [lang: string]: { title: string; internalDescription: string; citizenReason: string } }): void {
        for (const lang of this.langs) {
            const idx = (this.reasonsByLang[lang] || []).findIndex((r) => r.id === id);
            if (idx !== -1) {
                this.reasonsByLang[lang][idx] = {
                    ...this.reasonsByLang[lang][idx],
                    title: formValue[lang].title,
                    internalDescription: formValue[lang].internalDescription,
                    citizenReason: formValue[lang].citizenReason,
                };
            }
        }
        this.updateDataSource();
        this.save(this.translate.instant('pages.config-all.denial-reasons.messages.editSuccess'));
    }

    private deleteReason(id: string, dialogRef?: any): void {
        for (const lang of this.langs) {
            this.reasonsByLang[lang] = (this.reasonsByLang[lang] || []).filter((r) => r.id !== id);
            this.reasonsByLang[lang].forEach((r, i) => (r.order = i + 1));
        }
        this.updateDataSource();
        this.save(this.translate.instant('pages.config-all.denial-reasons.messages.deleteSuccess'), dialogRef);
    }

    private getMaxOrder(): number {
        const lang = this.langs[0];
        const reasons = this.reasonsByLang[lang] || [];
        return reasons.length > 0 ? Math.max(...reasons.map((r) => r.order)) : 0;
    }

    private save(successMessage: string, dialogRef?: any): void {
        this.saving = true;
        const denialReasons: { [lang: string]: DenialReason[] } = {};
        for (const lang of this.langs) {
            denialReasons[lang] = this.reasonsByLang[lang] || [];
        }

        let payload: PatchOrganisationConfiguration;

        if (this.group === 'support' && this.cardType && this.orgConfig) {
            const cards = this.orgConfig.support.cards.slice();
            const cardIndex = cards.findIndex((c) => c.type === this.cardType);
            if (cardIndex !== -1) {
                cards[cardIndex] = { ...cards[cardIndex], denialReasons };
            }
            payload = { group: 'support', data: { cards } };
        } else {
            payload = { group: this.group, data: { denialReasons } };
        }

        this.configAllService
            .patchOrganisationConfiguration(payload, this.orgTag)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.saving = false;
                    if (dialogRef) dialogRef.close();
                    this.contextService.successMessage(successMessage);
                    this.contextService.refreshOrganisationConfiguration().pipe(takeUntil(this.destroy$)).subscribe();
                },
                (error) => {
                    this.saving = false;
                    if (dialogRef) dialogRef.close();
                    this.contextService.errorMessage(this.translate.instant('pages.config-all.denial-reasons.messages.error'));
                    console.log(error);
                },
            );
    }
}
